<template>
  <v-dialog v-model="dialog" max-width="610px" persistent>
    <v-card class="assign-owner-modal" rounded="lg">
      <!-- Use h2 instead of v-card-title to avoid broker-app font override -->
      <div class="modal-header">
        <h2 class="modal-title">{{ $t('broker.clubs.assignOwner.title') }}</h2>
        <v-btn icon class="close-btn" @click="$emit('close')">
          <v-icon>fa-regular fa-xmark</v-icon>
        </v-btn>
      </div>

      <v-card-text class="modal-content">
        <p class="subtitle-text">
          {{ $t('broker.clubs.assignOwner.subtitle') }}
        </p>

        <!-- Name field -->
        <div class="form-field">
          <label class="field-label">{{ $t('broker.clubs.assignOwner.nameLabel') }}*</label>
          <v-text-field
            v-model="form.name"
            :placeholder="$t('broker.clubs.assignOwner.namePlaceholder')"
            outlined
            dense
            hide-details="auto"
            :error-messages="errors.name"
            @input="clearError('name')"
            @blur="validateName"
          />
        </div>

        <!-- Email field -->
        <div class="form-field">
          <label class="field-label">{{ $t('broker.clubs.assignOwner.emailLabel') }}*</label>
          <v-text-field
            v-model="form.email"
            :placeholder="$t('broker.clubs.assignOwner.emailPlaceholder')"
            outlined
            dense
            hide-details="auto"
            :error-messages="errors.email"
            @input="clearError('email')"
            @blur="validateEmail"
          />
        </div>

        <!-- Phone field (optional) -->
        <div class="form-field">
          <label class="field-label">{{ $t('broker.clubs.assignOwner.phoneLabel') }}</label>
          <v-text-field
            v-model="form.phone"
            :placeholder="$t('broker.clubs.assignOwner.phonePlaceholder')"
            outlined
            dense
            hide-details
            class="optional-field"
          />
        </div>

        <!-- Members limit dropdown -->
        <div class="form-field">
          <label class="field-label">{{ $t('broker.clubs.assignOwner.membersLimitLabel') }}</label>
          <v-select
            ref="membersLimitSelect"
            v-model="form.membersLimit"
            :items="membersLimitOptions"
            item-text="text"
            item-value="value"
            outlined
            dense
            hide-details
            append-icon="fa-regular fa-chevron-down"
            class="members-limit-select"
            :class="{ 'is-dirty': selectDirty }"
            @change="onSelectChange"
          />
        </div>

        <!-- Notify checkbox -->
        <div class="notify-checkbox" @click="form.notifyOnAccept = !form.notifyOnAccept">
          <span class="notify-label">{{ $t('broker.clubs.assignOwner.notifyCheckbox') }}</span>
          <div class="custom-checkbox" :class="{ checked: form.notifyOnAccept }">
            <v-icon v-if="form.notifyOnAccept" size="14" color="white">fa-solid fa-check</v-icon>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="modal-actions">
        <v-btn
          large
          rounded
          class="cancel-btn"
          min-width="140"
          height="40"
          @click="$emit('close')"
        >
          {{ $t('broker.clubs.assignOwner.cancelBtn') }}
        </v-btn>

        <v-btn
          large
          rounded
          class="send-btn"
          min-width="180"
          height="40"
          :loading="loading"
          :disabled="!isFormValid || loading"
          @click="sendInvitation"
        >
          {{ $t('broker.clubs.assignOwner.sendBtn') }}
          <v-icon right size="18">fa-regular fa-paper-plane</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import clubService from '../../services/club.service'

export default {
  name: 'AssignOwnerModal',
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      selectDirty: false,
      form: {
        name: '',
        email: '',
        phone: '',
        membersLimit: 50,
        notifyOnAccept: false
      },
      errors: {
        name: '',
        email: ''
      }
    }
  },
  computed: {
    dialog: {
      get() {
        return this.isOpen
      },
      set(value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    isFormValid() {
      return this.form.name.trim() !== '' && this.isValidEmail(this.form.email)
    },
    membersLimitOptions() {
      return [
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.members', { count: 25 }), value: 25 },
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.members', { count: 50 }), value: 50 },
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.members', { count: 100 }), value: 100 },
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.members', { count: 250 }), value: 250 },
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.members', { count: 500 }), value: 500 },
        { text: this.$t('broker.clubs.assignOwner.membersLimitOptions.unlimited'), value: -1 }
      ]
    }
  },
  methods: {
    isValidEmail(email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      return emailRegex.test(email)
    },

    clearError(field) {
      this.errors[field] = ''
    },

    validateName() {
      if (this.form.name.trim() === '') {
        this.errors.name = this.$t('broker.clubs.assignOwner.nameRequired')
      }
    },

    validateEmail() {
      if (this.form.email.trim() === '') {
        this.errors.email = this.$t('broker.clubs.assignOwner.emailRequired')
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = this.$t('broker.clubs.assignOwner.emailInvalid')
      }
    },

    validateForm() {
      let isValid = true

      if (!this.form.name.trim()) {
        this.errors.name = this.$t('broker.clubs.assignOwner.nameRequired')
        isValid = false
      }

      if (!this.form.email.trim()) {
        this.errors.email = this.$t('broker.clubs.assignOwner.emailRequired')
        isValid = false
      } else if (!this.isValidEmail(this.form.email)) {
        this.errors.email = this.$t('broker.clubs.assignOwner.emailInvalid')
        isValid = false
      }

      return isValid
    },

    async sendInvitation() {
      if (!this.validateForm()) {
        return
      }

      this.loading = true

      try {
        // Build form data for the API
        const formData = {
          name: this.form.name.trim(),
          email: this.form.email.trim(),
          phone: this.form.phone.trim() || null,
          membersLimit: this.form.membersLimit,
          notifyOnAccept: this.form.notifyOnAccept
        }

        // Call the real API
        await clubService.sendAdminInvitation(formData)

        // Emit success with the form data
        this.$emit('invitation-sent', {
          name: this.form.name,
          email: this.form.email
        })

        // Reset form
        this.resetForm()
      } catch (error) {
        console.error('Error sending invitation:', error)
        // Show error
        this.errors.email = error.message || 'Failed to send invitation. Please try again.'
      } finally {
        this.loading = false
      }
    },

    resetForm() {
      this.form = {
        name: '',
        email: '',
        phone: '',
        membersLimit: 50,
        notifyOnAccept: false
      }
      this.errors = {
        name: '',
        email: ''
      }
      this.selectDirty = false
      // Reset the select element styles
      this.$nextTick(() => {
        this.updateSelectStyles(false)
      })
    },

    onSelectChange() {
      this.selectDirty = true
      this.$nextTick(() => {
        this.updateSelectStyles(true)
      })
    },

    updateSelectStyles(isDirty) {
      // Apply inline styles to override global broker-app styles
      const selectRef = this.$refs.membersLimitSelect
      if (selectRef && selectRef.$el) {
        const selectionEl = selectRef.$el.querySelector('.v-select__selection')
        const fieldsetEl = selectRef.$el.querySelector('fieldset')
        const iconEl = selectRef.$el.querySelector('.v-input__append-inner .v-icon')

        if (isDirty) {
          if (selectionEl) selectionEl.style.setProperty('color', '#3B3B3B', 'important')
          if (fieldsetEl) fieldsetEl.style.setProperty('border-color', '#3B3B3B', 'important')
          if (iconEl) iconEl.style.setProperty('color', '#333', 'important')
        } else {
          if (selectionEl) selectionEl.style.removeProperty('color')
          if (fieldsetEl) fieldsetEl.style.removeProperty('border-color')
          if (iconEl) iconEl.style.removeProperty('color')
        }
      }
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal) {
        // Reset form when modal opens
        this.resetForm()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.assign-owner-modal {
  background-color: #F8F8F8 !important;
  padding: 0 !important;
}

.modal-header {
  position: relative;
  padding: 40px 40px 0 40px;
  padding-right: 60px;
}

.modal-title {
  font-family: 'Aeonik', sans-serif !important;
  font-size: 30px !important;
  font-weight: 700 !important;
  line-height: 40px !important;
  color: #14161B !important;
  word-break: break-word;
  margin: 0;
}

.close-btn {
  position: absolute;
  top: 20px;
  right: 20px;
}

.modal-content {
  padding: 10px 40px 0 40px !important;
}

.subtitle-text {
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: #14161B;
  margin-bottom: 40px;
}

.form-field {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #14161B;
  letter-spacing: 0.28px;
  margin-bottom: 0;
  line-height: 1.5;
}

.members-limit-select {
  ::v-deep .v-input__append-inner {
    .v-icon {
      font-size: 16px !important;
      color: #ABABAB;
      transition: color 0.2s ease;
    }
  }

  // Default state - light gray border
  ::v-deep fieldset {
    border-color: #ABABAB !important;
    transition: border-color 0.2s ease;
  }

  // Hover state (only when not dirty)
  &:not(.is-dirty):hover {
    ::v-deep fieldset {
      border-color: #3B3B3B !important;
    }
    ::v-deep .v-input__append-inner .v-icon {
      color: #333 !important;
    }
  }

  // Dirty state - keep dark borders
  &.is-dirty {
    ::v-deep fieldset {
      border-color: #3B3B3B !important;
    }
    ::v-deep .v-select__selection {
      color: #3B3B3B !important;
    }
    ::v-deep .v-input__append-inner .v-icon {
      color: #333 !important;
    }
  }
}

.notify-checkbox {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;
  padding: 0;
  margin-top: 20px;
}

.notify-label {
  font-family: 'Avenir LT Std', 'Avenir', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #000;
  letter-spacing: 0.28px;
}

.custom-checkbox {
  width: 20px;
  height: 20px;
  border: 2px solid #D9D9D9;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &.checked {
    background-color: #28EA96;
    border-color: #28EA96;
  }

  &:hover {
    border-color: #28EA96;
  }
}

.modal-actions {
  padding: 40px !important;
  display: flex;
  gap: 20px;
}

// Override broker-app global button styles
.cancel-btn.v-btn.v-btn {
  background-color: #EBEBEB !important;
  color: #14161B !important;
  text-transform: none !important;
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  height: 36px !important;
  flex: 1;
  box-shadow: none !important;

  &:hover {
    background-color: #D9D9D9 !important;
  }
}

.send-btn.v-btn.v-btn {
  background-color: #28EA96 !important;
  color: #005931 !important;
  text-transform: none !important;
  font-family: 'Aeonik', sans-serif !important;
  font-weight: 500 !important;
  font-size: 18px !important;
  letter-spacing: 0 !important;
  border-radius: 8px !important;
  height: 36px !important;
  flex: 1;
  box-shadow: none !important;

  &:hover {
    background-color: #1fd985 !important;
  }

  &.v-btn--disabled {
    background-color: #E8E8E8 !important;
    color: #9E9E9E !important;
  }
}

// Text field styling
::v-deep .v-text-field--outlined fieldset {
  border-color: #3B3B3B;
}

::v-deep .v-text-field--outlined.optional-field fieldset {
  border-color: #ABABAB;
}

::v-deep .v-text-field--outlined:hover fieldset {
  border-color: #3B3B3B;
}

::v-deep .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #28EA96;
  border-width: 2px;
}

::v-deep .v-text-field.v-text-field--outlined {
  .v-input__control {
    min-height: 35px !important;
  }

  .v-input__slot {
    min-height: 35px !important;
  }

  input {
    font-family: 'Avenir LT Std', 'Avenir', sans-serif;
    font-size: 16px;
    color: #3B3B3B;
    padding: 8px 16px !important;

    &::placeholder {
      color: #3B3B3B;
    }
  }
}

::v-deep .v-select.v-text-field--outlined {
  .v-input__slot {
    min-height: 35px !important;
    border-radius: 5px;
  }

  .v-select__selection {
    font-family: 'Avenir LT Std', 'Avenir', sans-serif;
    font-size: 16px;
    color: #ABABAB;
  }
}

// Error state for text fields
::v-deep .v-text-field--outlined.error--text {
  fieldset {
    border-color: #ff5252 !important;
  }
}
</style>
